import { Problem } from '@myrewards/contracts';
import type { z } from 'zod';
import { ApiError } from './errors';
import { recordRequest } from '../feedback/diagnostics';
import { reportBrowserFailure } from '../telemetry';
import { GENERATION_HEADER, markSetupPageStale, setupPage } from '../setup/page';

/**
 * The CSRF header (APP-006). The server refuses every unsafe request without it (403), and a
 * cross-site page can't add a custom header without a CORS preflight the server never grants.
 */
export const CSRF_HEADER = 'x-requested-with';
export const CSRF_VALUE = 'myrewards';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

const SAFE_METHODS: ReadonlySet<HttpMethod> = new Set(['GET']);

function responseRequestId(response: Response, initiationId: string): string {
  const id = response.headers.get('x-request-id');
  return id !== null &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)
    ? id.toLowerCase()
    : initiationId;
}

export interface RequestOptions<S extends z.ZodType> {
  /** The zod schema from `@myrewards/contracts` that the 2xx body must match. */
  schema: S;
  method?: HttpMethod;
  /** Sent as JSON, or as a multipart form when it is `FormData` (file uploads). */
  body?: unknown;
  signal?: AbortSignal;
  /** Sent as `Idempotency-Key`: a repeat of the same request is the same operation. */
  idempotencyKey?: string;
  /**
   * The browser finishes the request even if the page goes away meanwhile. For small writes only:
   * a browser allows 64 KiB of such bodies in flight at once.
   */
  keepalive?: boolean;
}

export interface DownloadOptions {
  method?: HttpMethod;
  /** JSON or FormData, with the same request semantics as `request`. */
  body?: unknown;
  /** Expected media type without parameters, for example `application/zip`. */
  contentType: string;
  /** Maximum decoded response bytes, independent of the Content-Length header. */
  maxBytes: number;
  signal?: AbortSignal;
  /**
   * Told the file's own name from its `Content-Disposition` (the UTF-8 `filename*` first), or null
   * when it gives none, once the response is a good one (APP-256: an export names itself).
   */
  onFileName?: (name: string | null) => void;
}

/** A file's name from a `Content-Disposition` header: `filename*` (UTF-8) first, then `filename`. */
export function fileNameOf(header: string | null): string | null {
  if (header === null) return null;
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (star?.[1] !== undefined) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // A malformed encoding falls through to the plain name.
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/.exec(header);
  const name = (plain?.[1] ?? plain?.[2] ?? '').trim();
  return name === '' ? null : name;
}

/**
 * What every unsafe request carries: the CSRF header, and in the setup sandbox the generation
 * this page was served in (APP-096), so a page from before a reset can't change the new one.
 */
function guardUnsafe(headers: Headers, method: HttpMethod): void {
  if (SAFE_METHODS.has(method)) return;
  headers.set(CSRF_HEADER, CSRF_VALUE);
  const setup = setupPage();
  if (setup !== null) headers.set(GENERATION_HEADER, setup.generation);
}

/** Same-origin paths only: "/api/v1/me", never "//host/x" or "https://…". */
function assertSameOriginPath(path: string): void {
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) {
    throw new TypeError(`API paths must be same-origin paths, got "${path}".`);
  }
}

/** By name: fetch implementations don't share one DOMException class. */
function isAbort(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error.name === 'AbortError' || error.name === 'TimeoutError')
  );
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return undefined;
  return JSON.parse(text) as unknown;
}

async function problemFrom(response: Response, requestId: string): Promise<ApiError> {
  let body: unknown;
  try {
    body = await readJson(response);
  } catch {
    body = undefined;
  }
  return problemError(response, body, requestId);
}

function problemError(response: Response, body: unknown, requestId: string): ApiError {
  const parsed = Problem.safeParse(body);
  if (parsed.success) {
    if (parsed.data.code === 'setup_page_stale') markSetupPageStale();
    return new ApiError(response.status, parsed.data.code, parsed.data.title, {
      problem: parsed.data,
      requestId,
    });
  }
  return new ApiError(
    response.status,
    'unexpected_response',
    `The server answered ${response.status} without the details we expected.`,
    { requestId },
  );
}

async function cancelDownload(response: Response, error: ApiError): Promise<never> {
  await response.body?.cancel().catch(() => undefined);
  throw error;
}

async function downloadParts(
  response: Response,
  maxBytes: number,
  tooLarge: () => ApiError,
  signal: AbortSignal | undefined,
  requestId: string,
): Promise<Uint8Array<ArrayBuffer>[]> {
  const reader = response.body?.getReader();
  if (reader === undefined) return [];
  const parts: Uint8Array<ArrayBuffer>[] = [];
  let size = 0;
  try {
    for (;;) {
      signal?.throwIfAborted();
      const chunk = await reader.read();
      signal?.throwIfAborted();
      if (chunk.done) return parts;
      if (chunk.value.byteLength > maxBytes - size) throw tooLarge();
      size += chunk.value.byteLength;
      if (chunk.value.byteLength > 0) parts.push(new Uint8Array(chunk.value));
    }
  } catch (error) {
    await reader.cancel().catch(() => undefined);
    if (isAbort(error) || error instanceof ApiError) throw error;
    throw new ApiError(0, 'network_error', 'We couldn’t finish downloading the file.', {
      cause: error,
      requestId,
    });
  } finally {
    reader.releaseLock();
  }
}

/** Authenticated, same-origin file response with a strict MIME and streamed byte budget. */
async function performDownload(
  path: string,
  { method = 'GET', body, contentType, maxBytes, signal, onFileName }: DownloadOptions,
): Promise<Blob> {
  assertSameOriginPath(path);
  // URL parsing also rejects authority changes hidden behind tabs/newlines in a path.
  const origin = 'https://myrewards.invalid';
  if (new URL(path, origin).origin !== origin)
    throw new TypeError('Download paths must be same-origin.');
  if (!Number.isSafeInteger(maxBytes) || maxBytes <= 0)
    throw new TypeError('Download maxBytes must be a positive safe integer.');
  const expectedType = contentType.toLowerCase();
  if (!/^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+-]+$/.test(expectedType))
    throw new TypeError('Download contentType must be a media type without parameters.');
  const initiationId = crypto.randomUUID();
  const headers = new Headers({
    accept: `${expectedType}, application/problem+json`,
    'x-request-id': initiationId,
  });
  guardUnsafe(headers, method);
  const payload: BodyInit | undefined =
    body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body);
  if (typeof payload === 'string') headers.set('content-type', 'application/json');

  let response: Response;
  const started = performance.now();
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      mode: 'same-origin',
      redirect: 'error',
      ...(payload !== undefined && { body: payload }),
      ...(signal !== undefined && { signal }),
    });
  } catch (error) {
    if (isAbort(error)) throw error;
    recordRequest({
      method,
      path,
      status: null,
      ms: performance.now() - started,
      requestId: initiationId,
    });
    throw new ApiError(0, 'network_error', 'We couldn’t reach MyRewards.', {
      cause: error,
      requestId: initiationId,
    });
  }
  recordRequest({
    method,
    path,
    status: response.status,
    ms: performance.now() - started,
    requestId: initiationId,
  });
  const requestId = responseRequestId(response, initiationId);

  if (!response.ok) {
    // Problems have their own small budget; the file limit must not hide a useful 4xx response.
    const parts = await downloadParts(
      response,
      64 * 1024,
      () => problemError(response, undefined, requestId),
      signal,
      requestId,
    );
    const decoder = new TextDecoder();
    const text =
      parts.map((part) => decoder.decode(part, { stream: true })).join('') + decoder.decode();
    let problem: unknown;
    try {
      problem = JSON.parse(text) as unknown;
    } catch {
      problem = undefined;
    }
    throw problemError(response, problem, requestId);
  }

  const tooLarge = () =>
    new ApiError(
      response.status,
      'download_too_large',
      'This download is too large. Choose fewer files.',
      { requestId },
    );
  if (response.headers.get('content-type')?.split(';')[0]?.trim().toLowerCase() !== expectedType)
    return cancelDownload(
      response,
      new ApiError(
        response.status,
        'invalid_response',
        'The server did not return the expected file type.',
        { requestId },
      ),
    );
  const length = response.headers.get('content-length');
  if (length !== null) {
    const size = Number(length);
    if (!/^\d+$/.test(length) || !Number.isSafeInteger(size))
      return cancelDownload(
        response,
        new ApiError(
          response.status,
          'invalid_response',
          'The server returned an invalid file size.',
          { requestId },
        ),
      );
    if (size > maxBytes) return cancelDownload(response, tooLarge());
  }
  const blob = new Blob(await downloadParts(response, maxBytes, tooLarge, signal, requestId), {
    type: expectedType,
  });
  onFileName?.(fileNameOf(response.headers.get('content-disposition')));
  return blob;
}

/**
 * The only place the web app talks to the server. Same origin, the session cookie goes along
 * (`credentials: 'same-origin'`), unsafe methods carry the CSRF header, failures become an
 * `ApiError` with the status and code from problem+json, and every 2xx body is validated
 * against its contract schema before anything renders it.
 */
async function performRequest<S extends z.ZodType>(
  path: string,
  { schema, method = 'GET', body, signal, idempotencyKey, keepalive }: RequestOptions<S>,
): Promise<z.output<S>> {
  assertSameOriginPath(path);
  const initiationId = crypto.randomUUID();
  const headers = new Headers({
    accept: 'application/json, application/problem+json',
    'x-request-id': initiationId,
  });
  guardUnsafe(headers, method);
  if (idempotencyKey !== undefined) headers.set('idempotency-key', idempotencyKey);
  // A multipart body sets its own content type, with the boundary; the browser adds it.
  const payload: BodyInit | undefined =
    body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body);
  if (typeof payload === 'string') headers.set('content-type', 'application/json');

  let response: Response;
  const started = performance.now();
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      ...(payload !== undefined && { body: payload }),
      ...(signal !== undefined && { signal }),
      ...(keepalive === true && { keepalive }),
    });
  } catch (error) {
    if (isAbort(error)) throw error;
    // For the feedback report's recent-requests ring (APP-104 §4); no answer means no status.
    recordRequest({
      method,
      path,
      status: null,
      ms: performance.now() - started,
      requestId: initiationId,
    });
    throw new ApiError(0, 'network_error', 'We couldn’t reach MyRewards.', {
      cause: error,
      requestId: initiationId,
    });
  }
  recordRequest({
    method,
    path,
    status: response.status,
    ms: performance.now() - started,
    requestId: initiationId,
  });
  const requestId = responseRequestId(response, initiationId);

  if (!response.ok) throw await problemFrom(response, requestId);

  let json: unknown;
  try {
    json = response.status === 204 ? undefined : await readJson(response);
  } catch (error) {
    if (isAbort(error)) throw error;
    throw new ApiError(response.status, 'invalid_response', 'The server’s answer wasn’t JSON.', {
      cause: error,
      requestId,
    });
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      response.status,
      'invalid_response',
      `The answer from ${method} ${path} didn’t match its contract.`,
      { cause: parsed.error, requestId },
    );
  }
  return parsed.data;
}

/** Observe direct callers too. An attempt may recover on retry; terminal cache events stay separate. */
async function observeAttempt<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (error instanceof ApiError) reportBrowserFailure(error, 'api_attempt_error');
    throw error;
  }
}

export function request<S extends z.ZodType>(
  path: string,
  options: RequestOptions<S>,
): Promise<z.output<S>> {
  return observeAttempt(() => performRequest(path, options));
}

export function requestDownload(path: string, options: DownloadOptions): Promise<Blob> {
  return observeAttempt(() => performDownload(path, options));
}
